/**
 * SubscribePage — Stripe Elements checkout for a specific tier.
 *
 * Route: /subscribe/:tier  (e.g. /subscribe/pro, /subscribe/team)
 * Query:  ?billing=monthly|yearly  (default: monthly)
 *
 * Single-step checkout for all paid plans. Credit card form appears alongside
 * registration fields from the first render — no separate "continue" step.
 *
 * Flow variants:
 *  1. Guest, paid tier  → all fields + PaymentElement in one screen
 *  2. Authenticated     → Elements only (user already known), fires on load
 *  3. Plan-change       → upgrade uses Stripe proration; downgrade → portal
 *  4. Enterprise        → redirect to ctaUrl (mailto: or external URL)
 *  5. Free tier, guest  → simple registration form, no Stripe
 *  6. Free tier, auth   → redirect to /dashboard
 *  7. Unknown tier      → redirect to /pricing
 *
 * @param tier    - Stripe product slug sourced from product.metadata.tier or normalised name
 * @param billing - "monthly" (default) | "yearly"
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  ShieldCheck,
  ArrowLeft,
  ArrowRight,
  Building2,
  ChevronsDown,
  Mail,
  Gift,
  Tag,
  CheckCircle2,
  ScrollText,
  PartyPopper,
} from 'lucide-react';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { useUserInfo } from '@/hooks/useUserInfo';
import { usePromoConfig } from '@/hooks/usePromoConfig';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
// EulaAccept (API-backed) is NOT used here — all checkout forms use the localStorage-only
// EulaScrollGate below. This avoids 401 errors in the guest (unauthenticated) checkout flow.
// The backend reads localStorage acceptance data during account provisioning.

// ---------------------------------------------------------------------------
// Stripe init (singleton — loadStripe deduplicates internally)
// ---------------------------------------------------------------------------

const STRIPE_KEY = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? '';
if (!STRIPE_KEY) {
  console.warn(
    '[SubscribePage] VITE_STRIPE_PUBLISHABLE_KEY is not set — ' +
      'add it to app/.env.local (pk_test_… from Stripe Dashboard → API keys).'
  );
}
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const STRIPE_APPEARANCE = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#10b981',
    colorBackground: '#1e293b',
    colorText: '#f1f5f9',
    colorDanger: '#f87171',
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
};

// sessionStorage key used to carry a pending checkout URL through the magic-link login flow.
const CHECKOUT_REDIRECT_KEY = 'guardrail_checkout_redirect';

// Tiers that show an organization name field
const TEAM_TIERS = new Set(['team', 'enterprise']);

/** Validate email format (basic RFC-5322 approximation). */
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// ---------------------------------------------------------------------------
// EulaScrollGate — standalone scroll-to-accept EULA gate (no API call).
// Used for guest flows (free + paid) where no auth token exists yet.
// The actual acceptance record is written post-login via the EulaAccept component.
// ---------------------------------------------------------------------------

const EULA_RAW_URL =
  'https://raw.githubusercontent.com/Guardrail-Documents/Licensing/main/aitrafficlight.com%20END-USER%20LICENSE%20AGREEMENT.md';

function EulaScrollGate({ accepted, onAccepted }: { accepted: boolean; onAccepted: () => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const { data: eulaText, isLoading } = useQuery({
    queryKey: ['eula-text'],
    queryFn: async () => {
      const resp = await fetch(EULA_RAW_URL);
      if (!resp.ok) throw new Error('Failed to load EULA');
      return resp.text();
    },
    staleTime: 60 * 60 * 1000,
    retry: 2,
  });

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20 && !scrolledToBottom) {
      setScrolledToBottom(true);
    }
  }, [scrolledToBottom]);

  // Auto-enable if content fits without scrolling (only after text loaded).
  // Defer via requestAnimationFrame so the browser has reflowed the rendered
  // text before we read scrollHeight — a sync read can return a stale value
  // and incorrectly mark the gate as satisfied before the user scrolls.
  useEffect(() => {
    if (!eulaText) return;
    const frame = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 20) {
        setScrolledToBottom(true);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [eulaText]);

  if (accepted) {
    return (
      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
        <span className="text-emerald-400 text-sm font-medium">License agreement accepted</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <ScrollText className="w-4 h-4 text-zinc-400" />
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
          End-User License Agreement
        </span>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-[200px] overflow-y-auto rounded-lg border border-zinc-600 bg-zinc-800/80 px-4 py-3 text-xs text-zinc-300 leading-relaxed font-mono scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
          </div>
        ) : (
          <pre className="whitespace-pre-wrap break-words">{eulaText}</pre>
        )}
      </div>
      {/* Scroll-to-bottom button — visible until user reaches the end */}
      {!scrolledToBottom && !isLoading && (
        <button
          type="button"
          onClick={() => {
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 border border-zinc-600 bg-zinc-700/60 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-medium transition-colors"
        >
          <ChevronsDown className="w-3.5 h-3.5" />
          Scroll to bottom
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          // Persist EULA acceptance to localStorage for guest flows.
          // The backend will read this when the account is provisioned.
          try {
            localStorage.setItem('guardrail_eula_accepted', JSON.stringify({
              acceptedAt: new Date().toISOString(),
              // Use a simple hash of the EULA text as the git hash proxy
              gitHash: eulaText ? btoa(eulaText.slice(0, 64)).slice(0, 12) : 'unknown',
            }));
          } catch { /* localStorage unavailable */ }
          onAccepted();
        }}
        disabled={!scrolledToBottom}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
      >
        Accept EULA
      </button>
      {!scrolledToBottom && !isLoading && (
        <p className="text-center text-[11px] text-zinc-500">
          Scrolling to the bottom is required.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FreeTierGuestSignup — simple email + name form; calls /v2/onboard directly.
// No Stripe Elements needed — the free subscription is auto-provisioned server-side.
//
// @param onSuccess - callback with the email used, shown in the "check email" state
// ---------------------------------------------------------------------------

interface FreeTierGuestSignupProps {
  onSuccess: (email: string) => void;
}

function FreeTierGuestSignup({ onSuccess }: FreeTierGuestSignupProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [eulaAcceptedLocal, setEulaAcceptedLocal] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !eulaAcceptedLocal) return;
    setLoading(true);
    setFormError(null);
    try {
      // Store marketing opt-in in localStorage alongside EULA data
      try {
        const stored = localStorage.getItem('guardrail_eula_accepted');
        const parsed = stored ? JSON.parse(stored) : {};
        localStorage.setItem('guardrail_eula_accepted', JSON.stringify({ ...parsed, marketingOptIn }));
      } catch { /* localStorage unavailable */ }
      // Read referral code from localStorage (persisted from ?ref=CODE on mount)
      let referrer: string | undefined;
      try { referrer = localStorage.getItem('guardrail_referral_code') || undefined; }
      catch { /* localStorage unavailable */ }

      await apiClient.onboard(name.trim(), email.trim(), undefined, referrer);
      onSuccess(email.trim());
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Signup failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="free-name">
          Full name
        </label>
        <input
          id="free-name"
          type="text"
          autoComplete="name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Smith"
          className="w-full rounded-lg bg-zinc-700/60 border border-zinc-600 px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="free-email">
          Email address
        </label>
        <input
          id="free-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full rounded-lg bg-zinc-700/60 border border-zinc-600 px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
        />
      </div>

      {/* EULA — must accept before registration */}
      <EulaScrollGate accepted={eulaAcceptedLocal} onAccepted={() => setEulaAcceptedLocal(true)} />

      {/* Marketing opt-in */}
      <label className="flex items-center justify-center gap-2.5 cursor-pointer group">
        <input
          type="checkbox"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-600 bg-zinc-700/60 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors leading-relaxed">
          I'd like to receive marketing offers and product updates
        </span>
      </label>

      {formError && <p className="text-red-400 text-sm">{formError}</p>}

      <button
        type="submit"
        disabled={loading || !name.trim() || !email.trim() || !eulaAcceptedLocal}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Create free account
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// TrialSuccessState — shown when intentType is 'none' (promo trial, no payment needed).
// Immediately provisions the account (creates KC user + sends magic link / auto-login),
// then navigates to the dashboard or shows "check your email".
// ---------------------------------------------------------------------------

interface TrialSuccessStateProps {
  subscriptionId: string;
  onBack: () => void;
}

function TrialSuccessState({ subscriptionId, onBack }: TrialSuccessStateProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTokens } = useAuth();
  const promo = usePromoConfig();
  const [provisionState, setProvisionState] = useState<'provisioning' | 'check_email' | 'error'>('provisioning');
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionError, setProvisionError] = useState('');
  const provisionCalledRef = useRef(false);

  // Provision account immediately — don't wait for a redirect to /checkout/return.
  // This ensures the KC user is created and magic link / auto-login tokens are issued
  // even if the user navigates away.
  useEffect(() => {
    if (provisionCalledRef.current) return;
    provisionCalledRef.current = true;

    (async () => {
      try {
        const result = await apiClient.guestProvision(subscriptionId, 'subscriptionId');
        if (result.email) setProvisionEmail(result.email);

        if (result.access_token) {
          // Auto-login: set tokens and redirect to dashboard
          setTokens(result.access_token, result.refresh_token);
          await queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
          navigate('/dashboard', { replace: true });
          return;
        }

        // No auto-login tokens — show "check your email" state
        setProvisionState('check_email');
      } catch (err) {
        console.error('[TrialSuccessState] guestProvision failed:', err);
        setProvisionError(err instanceof Error ? err.message : 'Account setup failed');
        setProvisionState('error');
        // Fallback: redirect to /checkout/return which has its own retry logic
        setTimeout(() => {
          navigate(`/checkout/return?subscription_id=${encodeURIComponent(subscriptionId)}`, {
            replace: true,
          });
        }, 3000);
      }
    })();
  }, [subscriptionId, navigate, queryClient, setTokens]);

  return (
    <div className="space-y-5 text-center">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-sm transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back
      </button>
      <div className="py-6 space-y-4">
        <PartyPopper className="w-10 h-10 text-emerald-400 mx-auto" />
        <div className="space-y-1">
          <p className="text-emerald-400 font-bold text-lg">You're all set!</p>
          <p className="text-zinc-300 text-sm">
            No payment required during the free trial.
          </p>
          <p className="text-zinc-500 text-xs mt-2">
            <span className="text-emerald-400 font-medium">{promo.trialLabel} free trial</span> · Cancel any time
          </p>
        </div>

        {provisionState === 'provisioning' && (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-zinc-400 mx-auto" />
            <p className="text-zinc-500 text-xs">Setting up your account…</p>
          </>
        )}

        {provisionState === 'check_email' && (
          <p className="text-zinc-300 text-sm">
            We sent a sign-in link to{' '}
            <span className="text-emerald-400 font-medium">{provisionEmail}</span>.
            Check your email to continue.
          </p>
        )}

        {provisionState === 'error' && (
          <>
            <p className="text-red-400 text-sm">{provisionError}</p>
            <p className="text-zinc-500 text-xs">Retrying automatically…</p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GuestCheckoutForm — two-step paid checkout for unauthenticated users.
//
// Step 1 — Info: name, email, org (team tiers), EULA scroll-to-accept, marketing opt-in.
//           On "Continue to Payment": POSTs to /v2/billing/guest-checkout which creates
//           a Stripe customer + subscription (default_incomplete) and returns
//           { clientSecret, subscriptionId, intentType, checkoutToken }.
//
// Step 2 — Payment: renders <Elements> with clientSecret, shows coupon code input
//           above PaymentElement. On 100% discount: hides PaymentElement, shows
//           "No payment required" banner. On submit: confirms payment via Stripe
//           Elements (or skips confirmation if 100% discount) and navigates to
//           /checkout/return?subscription_id=sub_xxx.
//
// @param tier      - Stripe product slug
// @param interval  - billing interval
// @param tierName  - human-readable tier name for the CTA button
// ---------------------------------------------------------------------------

interface GuestCheckoutFormProps {
  tier: string;
  interval: 'month' | 'year';
  tierName: string;
  price?: number;
  monthlyPrice?: number;
  yearlyPrice?: number;
}

/** State for the auto-magic-link flow triggered when a guest email already has an account. */
type MagicLinkFlow =
  | { state: 'none' }
  | { state: 'sending'; email: string }
  | { state: 'sent'; email: string }
  | { state: 'error'; email: string };

function GuestCheckoutForm({ tier, interval, tierName, monthlyPrice, yearlyPrice }: GuestCheckoutFormProps) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [magicLinkFlow, setMagicLinkFlow] = useState<MagicLinkFlow>({ state: 'none' });
  const [devSignupDone, setDevSignupDone] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Step 2 state — set after step 1 API call succeeds
  const [subData, setSubData] = useState<{
    clientSecret: string;
    subscriptionId: string;
    intentType: 'setup' | 'payment' | 'none';
    checkoutToken: string;
  } | null>(null);

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isDevMode = import.meta.env.DEV && !stripePromise;
  const isTeam = TEAM_TIERS.has(tier);
  const promo = usePromoConfig();

  // ── Step 1 submit: collect info, call guest-checkout, advance to step 2 ──
  const handleInfoSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValidEmail(email) || !firstName.trim() || !eulaAccepted) return;

      setLoading(true);
      setFormError(null);
      setMagicLinkFlow({ state: 'none' });

      try {
        // Read referral code from localStorage (persisted from ?ref=CODE on mount)
        let referrer: string | undefined;
        try { referrer = localStorage.getItem('guardrail_referral_code') || undefined; }
        catch { /* localStorage unavailable */ }

        // Dev mode without Stripe: create account via onboard and skip checkout
        if (isDevMode) {
          const fullName = `${firstName} ${lastName}`.trim() || 'Dev User';
          await apiClient.onboard(fullName, email, undefined, referrer);
          setDevSignupDone(true);
          return;
        }

        // Read EULA acceptance data from localStorage (written by EulaScrollGate)
        let eulaAcceptedAt: string | undefined;
        let eulaGitHash: string | undefined;
        try {
          const stored = localStorage.getItem('guardrail_eula_accepted');
          if (stored) {
            const parsed = JSON.parse(stored);
            eulaAcceptedAt = parsed.acceptedAt;
            eulaGitHash = parsed.gitHash;
          }
        } catch { /* localStorage unavailable */ }

        // Persist marketing opt-in alongside EULA data for guest-provision to read later
        try {
          const stored = localStorage.getItem('guardrail_eula_accepted');
          const parsed = stored ? JSON.parse(stored) : {};
          localStorage.setItem('guardrail_eula_accepted', JSON.stringify({ ...parsed, marketingOptIn }));
        } catch { /* localStorage unavailable */ }

        const result = await apiClient.guestCheckout({
          tier,
          interval,
          email,
          name: `${firstName} ${lastName}`.trim() || undefined,
          orgName: orgName || undefined,
          eulaAcceptedAt,
          eulaGitHash,
          marketingOptIn,
          referrer,
        });

        if ('clientSecret' in result && 'subscriptionId' in result && 'checkoutToken' in result) {
          // Success → advance to payment step (or skip to return if intentType is 'none')
          setSubData(result as {
            clientSecret: string;
            subscriptionId: string;
            intentType: 'setup' | 'payment' | 'none';
            checkoutToken: string;
          });
        } else if ('loginUrl' in result) {
          // This email already has an account — stash the current checkout URL and
          // send a magic link so the user can log in and return to this exact page.
          sessionStorage.setItem(
            CHECKOUT_REDIRECT_KEY,
            window.location.pathname + window.location.search
          );
          setMagicLinkFlow({ state: 'sending', email });
          try {
            await apiClient.sendMagicLink(email);
            setMagicLinkFlow({ state: 'sent', email });
          } catch {
            setMagicLinkFlow({ state: 'error', email });
          }
        } else if ('message' in result) {
          setFormError((result as { message?: string }).message ?? 'Failed to prepare checkout.');
        }
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to prepare checkout.');
      } finally {
        setLoading(false);
      }
    },
    [tier, interval, isDevMode, firstName, lastName, email, orgName, eulaAccepted, marketingOptIn]
  );

  const canContinue = isValidEmail(email) && firstName.trim().length > 0 && eulaAccepted && !loading;
  const billingName = `${firstName} ${lastName}`.trim();

  // ── Step 2a: no payment needed (trial promo — intentType 'none') ─────────
  if (subData && subData.intentType === 'none') {
    return (
      <TrialSuccessState
        subscriptionId={subData.subscriptionId}
        onBack={() => { setSubData(null); setFormError(null); }}
      />
    );
  }

  // ── Step 2b: payment form (rendered once subData is set with a clientSecret) ──
  if (subData && subData.clientSecret && stripePromise) {
    return (
      <div className="space-y-4">
        {/* Back to info step */}
        <button
          type="button"
          onClick={() => { setSubData(null); setFormError(null); }}
          className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-sm transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: subData.clientSecret, appearance: STRIPE_APPEARANCE }}
        >
          <PaymentForm
            subscriptionId={subData.subscriptionId}
            checkoutToken={subData.checkoutToken}
            intentType={subData.intentType as 'setup' | 'payment'}
            tierName={tierName}
            billingName={billingName}
            billingEmail={email}
            orgName={isTeam ? orgName : undefined}
            onError={setFormError}
          />
        </Elements>
        {formError && <p className="text-red-400 text-sm">{formError}</p>}
      </div>
    );
  }

  // ── Step 1: info collection form ─────────────────────────────────────────
  return (
    <form onSubmit={handleInfoSubmit} className="space-y-4">
      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3.5 space-y-1">
        <p className="text-emerald-400 text-sm font-semibold flex items-center gap-1.5">
          <Gift className="w-4 h-4 flex-shrink-0" />
          Free trial included
        </p>
        <p className="text-zinc-400 text-xs leading-relaxed">
          No credit card required to start. Your free trial lasts {promo.trialDays} days.
          We'll notify you before it expires so you can choose a plan.
        </p>
        {interval === 'month' && yearlyPrice != null && monthlyPrice != null && yearlyPrice > 0 && (
            <p className="text-zinc-400 text-xs pt-1">
              <a
                href={`/subscribe/${tier}?billing=yearly`}
                onClick={(e) => { e.preventDefault(); navigate(`/subscribe/${tier}?billing=yearly`); }}
                className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-medium"
              >
                Click here to save 40% with an annual subscription!
              </a>
            </p>
          )}
          {interval === 'year' && yearlyPrice != null && monthlyPrice != null && monthlyPrice > 0 && (
            <p className="text-emerald-400 text-xs font-medium pt-1">
              You're saving ${((monthlyPrice * 12) - yearlyPrice).toFixed(0)}/yr vs monthly billing!
            </p>
          )}
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="guest-fname">
            First name
          </label>
          <input
            id="guest-fname"
            type="text"
            autoComplete="given-name"
            required
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setTouched((p) => ({ ...p, firstName: true }))}
            placeholder="Jane"
            className={`w-full rounded-lg bg-zinc-700/60 border px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 ${touched.firstName && !firstName.trim() ? 'border-red-500/60' : 'border-zinc-600'}`}
          />
          {touched.firstName && !firstName.trim() && (
            <p className="text-red-400 text-xs mt-1">First name is required</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="guest-lname">
            Last name
          </label>
          <input
            id="guest-lname"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Smith"
            className="w-full rounded-lg bg-zinc-700/60 border border-zinc-600 px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="guest-email">
          Email address
        </label>
        <input
          id="guest-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (magicLinkFlow.state !== 'none') setMagicLinkFlow({ state: 'none' });
          }}
          onBlur={() => setTouched((p) => ({ ...p, email: true }))}
          placeholder="you@company.com"
          className={`w-full rounded-lg bg-zinc-700/60 border px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 ${touched.email && email && !isValidEmail(email) ? 'border-red-500/60' : 'border-zinc-600'}`}
        />
        {touched.email && email && !isValidEmail(email) && (
          <p className="text-red-400 text-xs mt-1">Please enter a valid email address</p>
        )}
      </div>

      {/* Org name — team/enterprise only */}
      {isTeam && (
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-1.5" htmlFor="guest-org">
            <Building2 className="inline w-3.5 h-3.5 mr-1 text-violet-400" />
            Organization name <span className="text-zinc-500 font-normal">(optional)</span>
          </label>
          <input
            id="guest-org"
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Acme Corp"
            className="w-full rounded-lg bg-zinc-700/60 border border-zinc-600 px-3.5 py-2.5 text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
          />
        </div>
      )}

      {/* EULA — must scroll and accept before continuing to payment */}
      <EulaScrollGate accepted={eulaAccepted} onAccepted={() => setEulaAccepted(true)} />

      {/* Marketing opt-in */}
      <label className="flex items-center justify-center gap-2.5 cursor-pointer group">
        <input
          type="checkbox"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-600 bg-zinc-700/60 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors leading-relaxed">
          I'd like to receive marketing offers and product updates
        </span>
      </label>

      {formError && magicLinkFlow.state === 'none' && (
        <p className="text-red-400 text-sm">{formError}</p>
      )}

      {/* Existing account → magic link states */}
      {magicLinkFlow.state === 'sending' && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        </div>
      )}
      {magicLinkFlow.state === 'sent' && (
        <div className="text-center space-y-4 py-4">
          <Mail className="w-8 h-8 text-emerald-400 mx-auto" />
          <p className="text-emerald-400 font-semibold">Check your email to continue</p>
          <p className="text-zinc-400 text-sm">
            We sent a sign-in link to{' '}
            <span className="text-zinc-200 font-medium">{magicLinkFlow.email}</span>. Click it to
            log in and return to this checkout automatically.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-emerald-400 text-sm hover:underline"
          >
            Or log in manually →
          </button>
        </div>
      )}
      {magicLinkFlow.state === 'error' && (
        <div className="text-center space-y-3 py-4">
          <p className="text-red-400 text-sm">
            Couldn&apos;t send the sign-in link.{' '}
            <a href="/login" className="underline hover:text-red-300">
              Log in here
            </a>{' '}
            and return to checkout.
          </p>
        </div>
      )}

      {/* Dev mode: account created */}
      {devSignupDone && (
        <div className="text-center space-y-4 py-4">
          <Mail className="w-8 h-8 text-sky-400 mx-auto" />
          <p className="text-sky-400 font-semibold">Check your email</p>
          <p className="text-zinc-400 text-sm">
            We sent a sign-in link to <span className="text-zinc-200 font-medium">{email}</span>.
          </p>
          <p className="text-amber-400/80 text-xs">
            Dev mode — Stripe checkout was skipped. Account created via onboard.
          </p>
        </div>
      )}

      {/* Existing account hint */}
      {magicLinkFlow.state === 'none' && !devSignupDone && (
        <p className="text-zinc-500 text-xs text-center">
          Already have an account?{' '}
          <a href="/login" className="text-emerald-400 hover:underline">
            Login here
          </a>
          .
        </p>
      )}

      {/* Continue to Payment button */}
      {magicLinkFlow.state === 'none' && !devSignupDone && (
        <button
          type="submit"
          disabled={!canContinue}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {loading ? 'Preparing payment…' : 'Continue to Payment'}
        </button>
      )}

      <p className="text-center text-xs text-zinc-500">
        Secured by{' '}
        <a
          href="https://stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-400 hover:text-zinc-200"
        >
          Stripe
        </a>{' '}
        · 256-bit encryption
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// PageHeader — must live outside SubscribePage to satisfy react-hooks/static-components
// ---------------------------------------------------------------------------

interface PageHeaderProps {
  onBack: () => void;
  isFree: boolean;
  tierName: string;
  interval: 'month' | 'year';
  price?: number;
}

function PageHeader({ onBack, isFree, tierName, interval, price }: PageHeaderProps) {
  const promo = usePromoConfig();
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-sm transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to pricing
      </button>

      <div className="flex items-center gap-3 mb-4">
        <TrafficLightLogo size="md" />
      </div>

      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0" />
        <h1 className="text-xl font-bold text-white">
          {isFree ? 'Start Free' : `Start Free — ${tierName}`}
        </h1>
      </div>

      {!isFree && (
        <p className="text-zinc-400 text-sm ml-9">
          {price != null && (
            <span className="text-white font-semibold">
              ${interval === 'year' ? price.toFixed(0) : price.toFixed(0)}/{interval === 'year' ? 'yr' : 'mo'}
            </span>
          )}
          {price != null && ' · '}
          Billed {interval === 'year' ? 'yearly' : 'monthly'} · Cancel any time
          <span className="ml-2 text-emerald-400 font-medium">
            · {promo.trialLabel} free trial
          </span>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaymentForm — inner Stripe Elements component; must live inside <Elements> provider.
//
// Used for both authenticated and guest paid-tier checkout. When billingName /
// billingEmail are provided (guest path) they are passed as billing_details so
// Stripe does not re-ask for them inside the PaymentElement.
//
// @param subscriptionId - Stripe subscription ID for the return URL
// @param intentType     - "setup" (trial) or "payment" (immediate charge)
// @param tierName       - human-readable tier name for the CTA button label
// @param interval       - billing interval used for promo copy
// @param billingName    - full name collected from the guest form (optional)
// @param billingEmail   - email collected from the guest form (optional)
// @param orgName        - org name to forward via return URL param (optional)
// @param onError        - propagate confirmation error message to parent
// ---------------------------------------------------------------------------

interface PaymentFormProps {
  subscriptionId: string;
  checkoutToken: string;
  intentType: 'setup' | 'payment';
  tierName: string;
  billingName?: string;
  billingEmail?: string;
  orgName?: string;
  onError: (msg: string) => void;
}

function PaymentForm({
  subscriptionId,
  checkoutToken,
  intentType,
  billingName,
  billingEmail,
  orgName,
  onError,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponApplied, setCouponApplied] = useState<string | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [fullDiscount, setFullDiscount] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);

  const handleApplyCoupon = useCallback(async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponApplying(true);
    setCouponError(null);
    setCouponApplied(null);
    try {
      const result = await apiClient.applyCoupon(subscriptionId, code, checkoutToken);
      if (result && 'applied' in result && result.applied) {
        setCouponApplied(result.discount?.label ?? 'Discount applied');
        setCouponCode('');
        // 100% percent-off coupon — no payment method needed
        if (result.discount?.type === 'percent' && result.discount?.value === 100) {
          setFullDiscount(true);
        }
      } else {
        setCouponError('Invalid or expired coupon code.');
      }
    } catch (err) {
      setCouponError(err instanceof Error ? err.message : 'Coupon could not be applied.');
    } finally {
      setCouponApplying(false);
    }
  }, [subscriptionId, checkoutToken, couponCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const orgParam = orgName ? `&org_name=${encodeURIComponent(orgName)}` : '';
    const returnUrl = `${window.location.origin}/checkout/return?subscription_id=${encodeURIComponent(subscriptionId)}${orgParam}`;

    // 100% discount — skip Stripe payment confirmation entirely
    if (fullDiscount) {
      navigate(`/checkout/return?subscription_id=${encodeURIComponent(subscriptionId)}${orgParam}`, {
        replace: true,
      });
      return;
    }

    if (!stripe || !elements) return;

    const confirmParams: Parameters<typeof stripe.confirmSetup>[0]['confirmParams'] = {
      return_url: returnUrl,
      ...(billingName || billingEmail
        ? { payment_method_data: { billing_details: { name: billingName, email: billingEmail } } }
        : {}),
    };

    // confirmSetup only accepts 'always'|'if_required'; confirmPayment redirect defaults to if_required.
    // Split the call so each branch carries the exact redirect type its overload accepts.
    let result: { error?: import('@stripe/stripe-js').StripeError };
    if (intentType === 'setup') {
      result = await stripe.confirmSetup({ elements, confirmParams, redirect: 'if_required' });
    } else {
      result = await stripe.confirmPayment({ elements, confirmParams });
    }

    if (result.error) {
      onError(result.error.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    navigate(`/checkout/return?subscription_id=${encodeURIComponent(subscriptionId)}${orgParam}`, {
      replace: true,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Coupon code — moved above payment form so users enter discount first */}
      {checkoutToken && (
        <div className="space-y-1.5">
          <label
            className="block text-xs font-medium text-zinc-400 uppercase tracking-wide"
            htmlFor="coupon-code"
          >
            <Tag className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
            Coupon code
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="coupon-code"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyCoupon();
                }
              }}
              placeholder="e.g. SUMMER40"
              disabled={!!couponApplied}
              className="flex-1 rounded-lg bg-zinc-700/60 border border-zinc-600 px-3.5 py-2 text-zinc-100 placeholder-zinc-500 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={!couponCode.trim() || couponApplying || !!couponApplied}
              className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-200 text-sm font-semibold transition-colors border border-zinc-600 whitespace-nowrap"
            >
              {couponApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Apply
            </button>
          </div>
          {couponApplied && (
            <p className="text-emerald-400 text-xs flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              {couponApplied}
            </p>
          )}
          {couponError && <p className="text-red-400 text-xs">{couponError}</p>}
        </div>
      )}

      {/* Payment form — hidden when coupon covers 100% of the cost */}
      {fullDiscount ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
          <p className="text-emerald-400 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Your coupon covers the full cost — no payment method required.
          </p>
        </div>
      ) : (
        <PaymentElement
          options={{
            layout: 'accordion',
            fields: {
              billingDetails: {
                name: billingName ? 'never' : 'auto',
                email: billingEmail ? 'never' : 'auto',
              },
            },
            defaultValues: {
              billingDetails: {
                name: billingName || undefined,
                email: billingEmail || undefined,
                address: { country: 'US' },
              },
            },
          }}
        />
      )}

      {/* EULA scroll-to-accept — uses localStorage (no API call).
          Works for both guest (no JWT) and authenticated flows.
          Backend reads localStorage acceptance during checkout/provisioning. */}
      <EulaScrollGate accepted={eulaAccepted} onAccepted={() => setEulaAccepted(true)} />

      {/* Marketing opt-in lives in the parent form (GuestCheckoutForm / authenticated wrapper),
          not here — avoids duplicate checkboxes when PaymentForm is nested inside a parent. */}

      <button
        type="submit"
        disabled={(!fullDiscount && (!stripe || !elements)) || submitting || !eulaAccepted}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {submitting ? 'Processing…' : fullDiscount ? 'Activate Plan' : 'Start Free'}
      </button>

      {!fullDiscount && (
        <p className="text-center text-xs text-zinc-500">
          Secured by{' '}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-zinc-400 hover:text-zinc-200"
          >
            Stripe
          </a>{' '}
          · 256-bit encryption
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SubscribePage() {
  useDocumentTitle('Subscribe');
  const { tier = '' } = useParams<{ tier: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authenticated, loading: authLoading, token } = useAuth();
  const promo = usePromoConfig();

  const interval: 'month' | 'year' = searchParams.get('billing') === 'yearly' ? 'year' : 'month';

  // ── Persist ?ref=CODE referral code to localStorage on mount ───────────
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      try { localStorage.setItem('guardrail_referral_code', ref); }
      catch { /* localStorage unavailable */ }
    }
  }, [searchParams]);

  // Resolve live tier data from the pricing API — no hardcoded slug maps.
  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ['pricing'],
    queryFn: () => apiClient.getPricing(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Current subscription info — used for plan-change context and ALREADY_SUBSCRIBED messaging.
  // Relies on the query cache populated by the dashboard; no extra network call when cached.
  const { data: userInfo } = useUserInfo();

  const resolvedTier = pricingData?.tiers.find((t) => t.tier === tier);
  const tierName = resolvedTier?.name ?? tier;
  const isEnterprise = resolvedTier?.ctaUrl?.startsWith('mailto:') ?? false;
  const isFree = resolvedTier ? resolvedTier.monthlyPrice === 0 : false;

  // Ref guard: toast fires once per page lifetime even under React StrictMode double-invoke.
  const planNotFoundToasted = useRef(false);

  // ── Guest: Free-tier provisional state (no Stripe needed) ─────────────
  const [freeSignupEmail, setFreeSignupEmail] = useState('');
  const [freeSignupDone, setFreeSignupDone] = useState(false);

  // ── Authenticated checkout session (Elements) ─────────────────────────────────
  // For authenticated users the subscription is created immediately on page load
  // (no org-name gate). The Elements form appears once the subscription is ready.

  const enabledForAuth =
    !authLoading && !pricingLoading && !!resolvedTier && !isEnterprise && !isFree && authenticated;
  // Guest paid-tier checkout is handled internally by GuestCheckoutForm;
  // the useQuery here is only for the authenticated path.

  const {
    data: sessionData,
    error: sessionError,
    isLoading: sessionLoading,
  } = useQuery({
    queryKey: ['checkout-session', tier, interval, 'auth'],
    queryFn: async (): Promise<{
      clientSecret: string;
      subscriptionId: string;
      intentType: 'setup' | 'payment' | 'none';
    }> => {
      const result = await apiClient.createEmbeddedCheckout(token!, { tier, interval });
      // Surface non-checkout responses as typed errors so the render can branch declaratively.
      if (result && 'code' in result) {
        const r = result as { code: string; message?: string };
        const err = Object.assign(new Error(r.message ?? 'Checkout unavailable'), { code: r.code });
        throw err;
      }
      return result as {
        clientSecret: string;
        subscriptionId: string;
        intentType: 'setup' | 'payment' | 'none';
      };
    },
    enabled: enabledForAuth && !!token,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  const clientSecret = sessionData?.clientSecret ?? null;
  const subscriptionId = sessionData?.subscriptionId ?? null;
  const intentType = sessionData?.intentType ?? 'setup';

  const [paymentError, setPaymentError] = useState<string | null>(null);

  const sessionErrorCode =
    sessionError && 'code' in (sessionError as object)
      ? (sessionError as unknown as { code: string }).code
      : null;

  // Only surface the authenticated checkout session error when the user IS authenticated.
  // A stale token can briefly fire the auth'd query before useAuth resolves → the 401
  // error must NOT leak into the guest checkout form.
  const error = authenticated && sessionError
    ? sessionErrorCode === 'ALREADY_SUBSCRIBED' || sessionErrorCode === 'DOWNGRADE_VIA_PORTAL'
      ? null // handled as a dedicated render branch, not an error screen
      : (sessionError as Error).message || 'Failed to start checkout.'
    : paymentError;

  // ── Render ──────────────────────────────────────────────────────────────
  // Enterprise: hand off to external CTA (mailto: or hosted sales page).
  // window.location.href must live in an effect (react-hooks/immutability).
  // Must be before any early returns to satisfy rules-of-hooks.
  useEffect(() => {
    if (isEnterprise && resolvedTier) {
      window.location.href = resolvedTier.ctaUrl;
    }
  }, [isEnterprise, resolvedTier]);

  // Fire-once toast when pricing data loaded but tier is unknown.
  // Must be in an effect — reading/writing .current in render is not allowed (react-hooks/refs).
  useEffect(() => {
    if (pricingData && !resolvedTier && !planNotFoundToasted.current) {
      planNotFoundToasted.current = true;
      toast.error('Plan not found', { description: 'Redirecting to pricing…' });
    }
  }, [pricingData, resolvedTier]);

  if (authLoading || pricingLoading) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex flex-col items-center gap-3 pt-40">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        <p className="text-sm text-zinc-400">Loading…</p>
      </div>
    );
  }

  // ── Guards (data-driven, render-path) ──────────────────────────────────
  if (isEnterprise && resolvedTier) {
    return null;
  }
  // Free + authenticated: account already exists — skip checkout.
  if (isFree && authenticated) return <Navigate to="/dashboard" replace />;
  // Unknown tier: bounce to pricing with a one-time toast.
  if (pricingData && !resolvedTier) {
    return <Navigate to="/pricing" replace />;
  }

  // Declarative early return: user is already on this exact tier.
  if (sessionErrorCode === 'ALREADY_SUBSCRIBED') {
    const periodEnd = userInfo?.subscription?.periodEnd;
    const cancelledOrEnding = userInfo?.subscription?.status === 'canceled';
    const renewLabel = periodEnd
      ? new Date(periodEnd).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="max-w-md text-center space-y-4 px-6">
          <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto" />
          <h2 className="text-white font-bold text-lg">You&apos;re already subscribed</h2>
          <p className="text-zinc-300">
            You&apos;re on the{' '}
            <span className="text-emerald-400 font-semibold">
              {userInfo?.subscription?.tierName || tierName}
            </span>{' '}
            plan.
            {renewLabel && (
              <span className="block text-zinc-400 text-sm mt-1">
                {cancelledOrEnding ? 'Access ends on ' : 'Renews on '}
                <span className="text-zinc-200 font-medium">{renewLabel}</span>.
              </span>
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
            <button
              onClick={() => navigate('/account?tab=subscription', { replace: true })}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
            >
              Manage subscription
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-sm font-semibold transition-colors"
            >
              View other plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Checkout errors are shown inline in the form below, not as a full-page redirect.

  if (!stripePromise && !import.meta.env.DEV) {
    return (
      <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center">
        <div className="max-w-md text-center space-y-3 px-6">
          <p className="text-amber-400 font-semibold">Stripe not configured</p>
          <p className="text-zinc-400 text-sm">
            Set <code className="text-zinc-300">VITE_STRIPE_PUBLISHABLE_KEY</code> in{' '}
            <code className="text-zinc-300">app/.env.local</code> and restart the dev server.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)]">
      <PageHeader
        onBack={() => navigate('/pricing')}
        isFree={isFree}
        tierName={tierName}
        interval={interval}
        price={interval === 'year' ? resolvedTier?.yearlyPrice : resolvedTier?.monthlyPrice}
      />

      <div className="max-w-xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-2xl bg-zinc-800/60 border border-zinc-700/60 p-4 sm:p-6 shadow-xl">
          {/* Promo banner — shown for authenticated paid-plan checkout, only if they haven't used a trial */}
          {authenticated && !isFree && (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3.5 space-y-1 mb-5">
              <p className="text-emerald-400 text-sm font-semibold flex items-center gap-1.5">
                <Gift className="w-4 h-4 flex-shrink-0" />
                Free trial included
              </p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                No credit card required to start. Your free trial lasts {promo.trialDays} days.
                We'll notify you before it expires so you can choose a plan.
              </p>
              {interval === 'month' && resolvedTier && resolvedTier.yearlyPrice > 0 && (
                <p className="text-zinc-400 text-xs pt-1">
                  <a
                    href={`/subscribe/${tier}?billing=yearly`}
                    onClick={(e) => { e.preventDefault(); navigate(`/subscribe/${tier}?billing=yearly`); }}
                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-medium"
                  >
                    Click here to save 40% with an annual subscription!
                  </a>
                </p>
              )}
              {interval === 'year' && resolvedTier && resolvedTier.monthlyPrice > 0 && (
                <p className="text-emerald-400 text-xs font-medium pt-1">
                  You're saving ${((resolvedTier.monthlyPrice * 12) - resolvedTier.yearlyPrice).toFixed(0)}/yr vs monthly billing!
                </p>
              )}
            </div>
          )}

          {/* Authenticated: loader while creating subscription */}
          {authenticated && !isFree && sessionLoading && (
            <div className="flex flex-col items-center gap-3 py-10">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
              <p className="text-sm text-zinc-400">Preparing your checkout…</p>
            </div>
          )}

          {/* Inline error — shown within the form instead of a full-page redirect */}
          {error && !sessionLoading && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4 space-y-2">
              <p className="text-red-400 text-sm font-semibold">Checkout unavailable</p>
              <p className="text-zinc-400 text-sm">{error}</p>
              <button
                onClick={() => navigate('/pricing')}
                className="text-emerald-400 text-xs hover:underline"
              >
                ← Choose a different plan
              </button>
            </div>
          )}

          {/* Plan-change context — shown when the user is moving between paid tiers */}
          {authenticated &&
            clientSecret &&
            userInfo?.subscription?.tier &&
            userInfo.subscription.tier !== 'free' &&
            userInfo.subscription.tier !== tier && (
              <div className="rounded-lg bg-zinc-700/40 border border-zinc-600/40 px-3.5 py-2.5 mb-4 text-xs text-zinc-400 text-center">
                Changing from{' '}
                <span className="text-zinc-200 font-medium">
                  {userInfo.subscription.tierName || userInfo.subscription.tier}
                </span>{' '}
                to <span className="text-emerald-400 font-medium">{tierName}</span>. Stripe will
                apply prorated credit or charge for the remainder of your billing period.
              </div>
            )}

          {/* Authenticated: trial promo — no payment needed (intentType 'none') */}
          {authenticated && subscriptionId && intentType === 'none' && !sessionLoading && (
            <TrialSuccessState
              subscriptionId={subscriptionId}
              onBack={() => navigate('/pricing')}
            />
          )}

          {/* Authenticated: Elements payment form */}
          {authenticated && clientSecret && subscriptionId && intentType !== 'none' && !sessionLoading && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
            >
              {/* authenticated path: server validates JWT, no checkout token needed */}
              <PaymentForm
                subscriptionId={subscriptionId}
                checkoutToken={''}
                intentType={intentType as 'setup' | 'payment'}
                tierName={tierName}
                onError={setPaymentError}
              />
            </Elements>
          )}

          {/* Authenticated: subscription active with no payment (e.g. $0 plan) */}
          {authenticated && subscriptionId && clientSecret === '' && intentType === 'setup' && !sessionLoading && (
            <div className="text-center space-y-4 py-4">
              <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto" />
              <p className="text-emerald-400 font-semibold">You&apos;re all set!</p>
              <button
                onClick={() =>
                  navigate(`/checkout/return?subscription_id=${encodeURIComponent(subscriptionId)}`)
                }
                className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
              >
                Go to dashboard
              </button>
            </div>
          )}

          {/* Guest, paid tier: single-step form with inline Elements */}
          {!authenticated && !isFree && (
            <GuestCheckoutForm
              tier={tier}
              interval={interval}
              tierName={tierName}
              price={interval === 'year' ? resolvedTier?.yearlyPrice : resolvedTier?.monthlyPrice}
              monthlyPrice={resolvedTier?.monthlyPrice}
              yearlyPrice={resolvedTier?.yearlyPrice}
            />
          )}

          {/* Guest, free tier: simple registration — no Stripe needed */}
          {!authenticated && isFree && !freeSignupDone && (
            <FreeTierGuestSignup
              onSuccess={(em) => {
                setFreeSignupEmail(em);
                setFreeSignupDone(true);
              }}
            />
          )}

          {/* Guest, free tier: account created */}
          {!authenticated && isFree && freeSignupDone && (
            <div className="text-center space-y-4 py-4">
              <Mail className="w-8 h-8 text-sky-400 mx-auto" />
              <p className="text-sky-400 font-semibold">Check your email</p>
              <p className="text-zinc-400 text-sm">
                We sent a sign-in link to{' '}
                <span className="text-zinc-200 font-medium">{freeSignupEmail}</span>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
